// src/components/tracking/SessionTracker.jsx
import {
  useEffect,
} from "react";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../AuthContext";

const HEARTBEAT_MS = 30000;

const SESSION_ID_KEY =
  "session_id";

const SESSION_USER_KEY =
  "session_user_id";

function clearLocalTrackingSession() {
  localStorage.removeItem(
    SESSION_ID_KEY
  );

  localStorage.removeItem(
    SESSION_USER_KEY
  );
}

/**
 * Returns a general device category without generating
 * a permanent device fingerprint.
 */
function detectDeviceType() {
  if (
    typeof navigator ===
    "undefined"
  ) {
    return "unknown";
  }

  const userAgent = String(
    navigator.userAgent || ""
  );

  // Supported by some Chromium-based browsers.
  if (
    navigator.userAgentData
      ?.mobile === true
  ) {
    return "mobile";
  }

  const isTablet =
    /iPad|Tablet|Kindle|Silk|PlayBook/i.test(
      userAgent
    ) ||
    (/Android/i.test(
      userAgent
    ) &&
      !/Mobile/i.test(
        userAgent
      ));

  if (isTablet) {
    return "tablet";
  }

  const isMobile =
    /Mobile|Mobi|iPhone|iPod|Windows Phone/i.test(
      userAgent
    );

  if (isMobile) {
    return "mobile";
  }

  if (userAgent) {
    return "desktop";
  }

  return "unknown";
}

/**
 * Detect the specific foreign-key failure we care about:
 *
 * the browser is trying to write tracking data for a
 * user_id that no longer exists in auth.users.
 */
function isMissingAuthUserForeignKey(
  error
) {
  if (!error) return false;

  if (
    String(error?.code || "") !==
    "23503"
  ) {
    return false;
  }

  const text = [
    error?.message,
    error?.details,
    error?.hint,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    text.includes(
      "user_sessions_user_id_fkey"
    ) ||
    text.includes(
      "session_heartbeats_user_id_fkey"
    ) ||
    (text.includes(
      "user_id"
    ) &&
      text.includes(
        "users"
      ))
  );
}

/**
 * If the database proves that the current Auth user no
 * longer exists, immediately stop tracking and remove
 * the stale local Supabase Auth session.
 */
async function handleMissingAuthUser(
  source,
  error
) {
  console.warn(
    `${source}: stale Auth user detected. Clearing local session.`,
    error
  );

  clearLocalTrackingSession();

  try {
    const {
      error: signOutError,
    } =
      await supabase.auth.signOut({
        scope: "local",
      });

    if (signOutError) {
      console.warn(
        `${source}: local Supabase signOut warning:`,
        signOutError
      );
    }
  } catch (signOutError) {
    console.warn(
      `${source}: local Supabase signOut failed:`,
      signOutError
    );
  }
}

/**
 * Confirm that Supabase Auth itself currently recognises
 * this user before we create tracking rows.
 */
async function isCurrentAuthUserValid(
  expectedUserId
) {
  if (!expectedUserId) {
    return false;
  }

  try {
    const {
      data,
      error,
    } =
      await supabase.auth.getUser();

    if (error) {
      console.warn(
        "SessionTracker auth validation failed:",
        error
      );

      return false;
    }

    const validatedUserId =
      data?.user?.id ?? null;

    return (
      validatedUserId ===
      expectedUserId
    );
  } catch (error) {
    console.warn(
      "SessionTracker auth validation error:",
      error
    );

    return false;
  }
}

/**
 * Update one known tracking session as ended.
 *
 * This is kept separate so starting a replacement session
 * does not accidentally clear the replacement's local keys.
 */
async function endSessionById(
  sessionId
) {
  if (!sessionId) return;

  try {
    const now =
      new Date().toISOString();

    const { error } =
      await supabase
        .from(
          "user_sessions"
        )
        .update({
          ended_at: now,
          last_seen_at: now,
        })
        .eq(
          "session_id",
          sessionId
        );

    if (error) {
      throw error;
    }
  } catch (error) {
    /**
     * Ending an old session is best-effort.
     *
     * For example, if the stored session belongs to another
     * account, RLS may correctly prevent the update.
     */
    console.warn(
      "endSessionById: could not end previous session",
      error
    );
  }
}

/**
 * Start a database tracking session for a user.
 *
 * Important:
 * localStorage is only updated AFTER the database insert
 * succeeds.
 */
async function startSessionForUser(
  userId,
  {
    shouldCommit = () =>
      true,
  } = {}
) {
  if (!userId) return null;

  /**
   * Independent safety check.
   *
   * Even if something upstream accidentally exposes a
   * cached user, SessionTracker will not write anything
   * until Supabase Auth validates that user.
   */
  const authUserIsValid =
    await isCurrentAuthUserValid(
      userId
    );

  if (!authUserIsValid) {
    console.warn(
      "startSessionForUser: refusing to create tracking session for an unverified Auth user"
    );

    clearLocalTrackingSession();
    return null;
  }

  if (!shouldCommit()) {
    return null;
  }

  const existingSessionId =
    localStorage.getItem(
      SESSION_ID_KEY
    );

  const existingUserId =
    localStorage.getItem(
      SESSION_USER_KEY
    );

  /**
   * Only try to remotely end the previous session if
   * it belongs to the SAME authenticated user.
   *
   * If it belongs to an old/different account, RLS would
   * correctly reject the update anyway.
   */
  if (
    existingSessionId &&
    existingUserId === userId
  ) {
    await endSessionById(
      existingSessionId
    );
  }

  /**
   * Whatever was locally stored is now superseded.
   */
  clearLocalTrackingSession();

  if (!shouldCommit()) {
    return null;
  }

  const sessionId =
    crypto.randomUUID();

  try {
    const now =
      new Date().toISOString();

    const { error } =
      await supabase
        .from(
          "user_sessions"
        )
        .insert({
          user_id: userId,
          session_id:
            sessionId,
          started_at: now,
          last_seen_at: now,
          user_agent:
            typeof navigator !==
            "undefined"
              ? navigator.userAgent ||
                ""
              : "",
          platform:
            typeof navigator !==
            "undefined"
              ? navigator.platform ||
                ""
              : "",
          device_type:
            detectDeviceType(),
          app_version:
            process.env
              .REACT_APP_BUILD ||
            null,
        });

    if (error) {
      throw error;
    }

    /**
     * Auth changed while the insert was happening.
     *
     * Do not commit this session to localStorage.
     */
    if (!shouldCommit()) {
      await endSessionById(
        sessionId
      );

      return null;
    }

    /**
     * Only persist the tracking identifiers AFTER
     * Supabase accepted the row.
     */
    localStorage.setItem(
      SESSION_ID_KEY,
      sessionId
    );

    localStorage.setItem(
      SESSION_USER_KEY,
      userId
    );

    return sessionId;
  } catch (error) {
    clearLocalTrackingSession();

    if (
      isMissingAuthUserForeignKey(
        error
      )
    ) {
      await handleMissingAuthUser(
        "startSessionForUser",
        error
      );

      return null;
    }

    console.error(
      "startSessionForUser: failed to insert user_session",
      error
    );

    return null;
  }
}

/**
 * End the current database tracking session.
 */
async function endCurrentSession({
  clearLocal = true,
} = {}) {
  const sessionId =
    localStorage.getItem(
      SESSION_ID_KEY
    );

  if (sessionId) {
    await endSessionById(
      sessionId
    );
  }

  if (clearLocal) {
    clearLocalTrackingSession();
  }
}

export default function SessionTracker() {
  const {
    user,
    session,
    loading,
  } = useAuth();

  /**
   * AuthContext has already validated the user.
   *
   * We additionally require the validated session/user IDs
   * to agree before tracking is allowed to run.
   */
  const activeUserId =
    !loading &&
    user?.id &&
    session?.user?.id ===
      user.id
      ? user.id
      : null;

  /**
   * Start the tracking session only after AuthContext
   * has completed server-side user validation.
   */
  useEffect(() => {
    let cancelled = false;

    if (!activeUserId) {
      clearLocalTrackingSession();

      return () => {
        cancelled = true;
      };
    }

    (async () => {
      await startSessionForUser(
        activeUserId,
        {
          shouldCommit: () =>
            !cancelled,
        }
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [activeUserId]);

  /**
   * Update the latest activity every 30 seconds.
   *
   * Unlike the old version, the heartbeat interval does not
   * run for a cached local user after AuthContext has
   * rejected that user.
   */
  useEffect(() => {
    if (!activeUserId) {
      return undefined;
    }

    let heartbeatInFlight =
      false;

    const sendHeartbeat =
      async () => {
        if (
          heartbeatInFlight
        ) {
          return;
        }

        const sessionId =
          localStorage.getItem(
            SESSION_ID_KEY
          );

        const trackedUserId =
          localStorage.getItem(
            SESSION_USER_KEY
          );

        /**
         * Never send tracking data for another/stale user.
         */
        if (
          !sessionId ||
          !trackedUserId ||
          trackedUserId !==
            activeUserId
        ) {
          return;
        }

        heartbeatInFlight =
          true;

        try {
          const now =
            new Date().toISOString();

          const {
            error:
              heartbeatError,
          } =
            await supabase
              .from(
                "session_heartbeats"
              )
              .upsert(
                {
                  user_id:
                    activeUserId,
                  session_id:
                    sessionId,
                  created_at:
                    now,
                },
                {
                  onConflict:
                    "user_id",
                }
              );

          if (
            heartbeatError
          ) {
            throw heartbeatError;
          }

          const {
            error:
              sessionError,
          } =
            await supabase
              .from(
                "user_sessions"
              )
              .update({
                last_seen_at:
                  now,
              })
              .eq(
                "session_id",
                sessionId
              );

          if (sessionError) {
            throw sessionError;
          }
        } catch (error) {
          if (
            isMissingAuthUserForeignKey(
              error
            )
          ) {
            await handleMissingAuthUser(
              "Session heartbeat",
              error
            );

            return;
          }

          console.error(
            "Session heartbeat error:",
            error
          );
        } finally {
          heartbeatInFlight =
            false;
        }
      };

    const timer =
      window.setInterval(
        sendHeartbeat,
        HEARTBEAT_MS
      );

    return () => {
      window.clearInterval(
        timer
      );
    };
  }, [activeUserId]);

  /**
   * Best-effort close when leaving the page.
   *
   * Browsers do not guarantee completion of async requests
   * during beforeunload, so this must never be relied on for
   * authentication correctness.
   */
  useEffect(() => {
    const handleBeforeUnload =
      () => {
        endCurrentSession({
          clearLocal: true,
        });
      };

    window.addEventListener(
      "beforeunload",
      handleBeforeUnload
    );

    return () => {
      window.removeEventListener(
        "beforeunload",
        handleBeforeUnload
      );
    };
  }, []);

  return null;
}